import dayjs from 'dayjs';

import { DATE_FORMAT } from './constants';

// Date-only values (and UTC midnight, which is how a date column is often sent)
// are a calendar day. Parsing them with `new Date('YYYY-MM-DD')` shifts them
// to the previous day in US timezones.
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:(?:T|\s)00:00:00(?:\.\d+)?Z?)?$/;
const US_CALENDAR_DAY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export const parseCalendarDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const iso = CALENDAR_DAY.exec(trimmed) ?? /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso && CALENDAR_DAY.test(trimmed)) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date
      : null;
  }
  const us = US_CALENDAR_DAY.exec(trimmed);
  if (us) {
    const month = Number(us[1]);
    const day = Number(us[2]);
    const year = Number(us[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date
      : null;
  }
  return null;
};

const asCalendarDay = (value: string) => {
  const date = CALENDAR_DAY.test(value.trim()) ? parseCalendarDate(value) : null;
  return date ? dayjs(date) : null;
};

const formatDate = (date?: string | Date | null, format: string = DATE_FORMAT): string => {
  if (!date) return '';
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  let parsedDate = (typeof date === 'string' ? asCalendarDay(date) : null) ?? dayjs(date);

  // If date is invalid,extracting first 8 characters and creating a new Date
  if (!parsedDate.isValid() && dateStr.length >= 8) {
    const extractedDate = dateStr.substring(0, 8);
    const formattedDate = `${extractedDate.substring(
      0,
      4
    )}-${extractedDate.substring(4, 6)}-${extractedDate.substring(6, 8)}`;
    parsedDate = dayjs(formattedDate, 'YYYY-MM-DD');
  }

  return parsedDate.isValid() ? parsedDate.format(format) : '';
};

export const formatUtcDate = (
  date?: string | Date | null,
  format: string = DATE_FORMAT
): string => {
  if (!date) return '';
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  let parsedDate = dayjs.utc(date);

  // If date is invalid,extracting first 8 characters and creating a new Date
  if (!parsedDate.isValid() && dateStr.length >= 8) {
    const extractedDate = dateStr.substring(0, 8);
    const formattedDate = `${extractedDate.substring(
      0,
      4
    )}-${extractedDate.substring(4, 6)}-${extractedDate.substring(6, 8)}`;
    parsedDate = dayjs.utc(formattedDate, 'YYYY-MM-DD');
  }

  return parsedDate.isValid() ? parsedDate.format(format) : '';
};

export default formatDate;

export function encodedDateToDecode(dateString: string): string {
  try {
    if (!dateString) {
      return 'Invalid Date';
    }

    // Check if the string is encoded
    const decodedDate = dateString.includes('%') ? decodeURIComponent(dateString) : dateString;

    // Convert to a valid date format
    const formattedDate = dayjs(decodedDate).isValid()
      ? dayjs(decodedDate).format('MM/DD/YYYY hh:mm A')
      : 'Invalid Date';

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

export function formatDateString(dateString: string | null | undefined): string {
  if (dateString === null) return '';

  // Check if the string is empty or invalid
  if (!dateString || typeof dateString !== 'string') {
    return 'Invalid Date';
  }

  const mmddyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  if (mmddyyyyRegex.test(dateString)) {
    return dateString; // Return as is if already in MM/DD/YYYY format
  }

  // Extract YYYY, MM, DD using regex
  // This will work for both formats: 2025-02-03 17:43:56.000 and 2025-03-04T23:35:00.000Z
  const regex = /^(\d{4})-(\d{2})-(\d{2})/;
  const match = dateString.match(regex);

  if (!match) {
    return 'Invalid Date';
  }

  // Extract year, month, and day from the regex match
  const [_, year, month, day] = match;

  // Return the formatted date string in MM/DD/YYYY format
  return `${month}/${day}/${year}`;
}

export const calculateAge = (birthDate: string): number => {
  const today = dayjs();
  const birth = asCalendarDay(birthDate) ?? dayjs(birthDate);

  if (!birth.isValid()) return 0;

  let age = today.year() - birth.year();

  // Adjust age if birthday hasn't occurred this year
  if (
    today.month() < birth.month() ||
    (today.month() === birth.month() && today.date() < birth.date())
  ) {
    age--;
  }

  return Math.max(0, age);
};

export const formatDateWithAge = (birthDate: string, format: string = 'MM/DD/YYYY'): string => {
  if (!birthDate) return '';

  const formattedDate = formatDate(birthDate, format);
  const age = calculateAge(birthDate);

  return formattedDate ? `${formattedDate} (${age} Years)` : '';
};
